import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators, FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { CountryISO, SearchCountryField, PhoneNumberFormat } from 'ngx-intl-tel-input';
import { AccountSelectorComponent } from '../../../../../../_shared/components/account-selector/account-selector.component';
import { FormErrorMessageComponent } from '../../../../../../_shared/components/form-error-message/form-error-message.component';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { Account } from '../../../../../../_shared/models';
import { SuppliersService } from '../../../services/suppliers.service';
import { Supplier, SupplierCategory } from '../../../models/supplier.model';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-upsert-supplier-modal',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, FormsModule, AccountSelectorComponent, FormErrorMessageComponent, NgxIntlTelInputModule, NgbDropdownModule, TranslocoDirective, TranslocoPipe],
  templateUrl: './upsert-supplier-modal.component.html',
  styleUrl: './upsert-supplier-modal.component.scss'
})
export class UpsertSupplierModalComponent implements OnInit {
  private fb: FormBuilder = inject(FormBuilder);
  private modalInstance: NgbActiveModal = inject(NgbActiveModal);
  private toastr: ToastrService = inject(ToastrService);
  private suppliersService: SuppliersService = inject(SuppliersService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService)
  private transloco: TranslocoService = inject(TranslocoService);

  supplier?: Supplier;

  supplierForm: FormGroup;
  selectedAccount: Account;
  submitted: boolean = false;
  isLoading: boolean = false;
  isCategoriesLoading: boolean = true;

  searchCountryField = [SearchCountryField.All];
  selectedCountryISO = CountryISO.SouthAfrica;
  phoneNumberFormat = PhoneNumberFormat.International;
  categories: SupplierCategory[] = [];
  readonly categoryPlaceholder: string = this.transloco.translate('suppliers.modal.dropdown.selectCategory');

  get isEditMode(): boolean {
    return !!this.supplier;
  }

  get modalTitle(): string {
    return this.transloco.translate(this.isEditMode ? 'suppliers.modal.editTitle' : 'suppliers.modal.addTitle');
  }

  get successMessage(): string {
    return this.transloco.translate(this.isEditMode ? 'suppliers.modal.toasts.updated' : 'suppliers.modal.toasts.added');
  }

  ngOnInit(): void {
    this.initForm();
    this.supplierForm.disable();
    this.getCategories();
  }

  private async getCategories(): Promise<void> {
    try {
      const response = await this.suppliersService.getSupplierCategoriesAsync();
      this.categories = response.data ?? [];

      if (this.isEditMode) {
        this.populateForm();
      }
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    } finally {
      this.isCategoriesLoading = false;
    }
  }

  private initForm(): void {
    this.supplierForm = this.fb.group({
      name: ['', Validators.required],
      category: ['', Validators.required],
      contactPerson: ['', Validators.required],
      phoneNumber: ['', [Validators.required, this.phoneNumberValidator()]],
      email: ['', [Validators.required, Validators.email]],
      note: ['']
    });
  }

  private populateForm(): void {
    if (!this.supplier) return;

    const category = this.categories.find(c => c.id === this.supplier!.categoryId);

    this.supplierForm.patchValue({
      name: this.supplier.name,
      contactPerson: this.supplier.contactPerson,
      phoneNumber: this.supplier.phoneNumber,
      email: this.supplier.email,
      note: this.supplier.note,
      category: category || ''
    });

    this.supplierForm.enable();
  }

  onSelectAccount(account: Account): void {
    this.selectedAccount = account;

    if (this.selectedAccount) {
      this.supplierForm.enable();
    }
  }

  onCategorySelect(category: SupplierCategory): void {
    const categoryControl = this.supplierForm.get('category');
    categoryControl?.setValue(category);
    categoryControl?.markAsTouched();
    categoryControl?.updateValueAndValidity();
  }

  getSelectedCategoryLabel(): string {
    if (this.isCategoriesLoading) return this.transloco.translate('suppliers.modal.dropdown.loadingCategories');
    const value = this.supplierForm.get('category')?.value;
    return value?.name || this.categoryPlaceholder;
  }

  async onSubmit(): Promise<void> {
    this.submitted = true;
    this.isLoading = true;

    try {
      const phoneValue = this.supplierForm.get('phoneNumber')?.value;
      const selectedCategory: SupplierCategory = this.supplierForm.get('category')?.value;
      const payload = {
        id: this.supplier?.id,
        name: this.supplierForm.get('name')?.value || '',
        categoryId: selectedCategory.id,
        contactPerson: this.supplierForm.get('contactPerson')?.value || '',
        phoneNumber: phoneValue?.internationalNumber || phoneValue?.number || phoneValue || '',
        email: this.supplierForm.get('email')?.value || '',
        note: this.supplierForm.get('note')?.value || '',
        accountId: this.isEditMode ? this.supplier!.accountId : this.selectedAccount.id
      };
      
      await this.suppliersService.upsertSupplierAsync(payload);
      this.toastr.success(this.successMessage);
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
          this.modalInstance.close(true);
        }
      });
    } finally {
      this.isLoading = false;
      this.modalInstance.close(true);
    }
  }

  getErrorMessage(field: string): string | null {
    const control = this.supplierForm.get(field);

    if (control?.hasError('required')) {
      return this.transloco.translate('suppliers.modal.errors.required');
    }

    if (field === 'email' && control?.hasError('email')) {
      return this.transloco.translate('suppliers.modal.errors.invalidEmail');
    }

    if (field === 'phoneNumber' && control?.value && control?.hasError('invalidPhone')) {
      return this.transloco.translate('suppliers.modal.errors.invalidPhone');
    }

    return null;
  }

  closeModal(): void {
    this.modalInstance.dismiss();
  }

  private phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }

      if (typeof value === 'object' && 'valid' in value) {
        return value.valid ? null : { invalidPhone: true };
      }

      return null;
    };
  }
}